import { useAuthHydration } from '@/hooks/use-auth-hydration';
import api from '@/services/api';
import { PRICE_DISPLAY_MODE } from '@/types/price-display-mode';
import { QUOTA_PERIOD } from '@/types/quota-period';
import { RECURRENCE_INTERVAL } from '@/types/recurrence-interval';
import { RECURRING_BOOKING_MODE } from '@/types/recurring-booking-mode';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

/**
 * Espelha `ServiceTemplateResponseDto`: o modelo guarda a configuração
 * inteira do serviço. Enquanto só nome, preço e endereço trafegavam aqui,
 * aplicar um modelo deixava duração, vagas e recorrência para o prestador
 * refazer na mão.
 */
export type ServiceTemplate = {
  id: string;
  name: string;
  description: string;
  defaultPrice?: number;
  priceDisplayMode?: PRICE_DISPLAY_MODE;
  address?: string;
  allowedPaymentMethods: string[];
  durationMinutes?: number | null;
  slotCapacity?: number;
  isSubscription?: boolean;
  subscriptionQuota?: number | null;
  subscriptionQuotaPeriod?: QUOTA_PERIOD | null;
  recurringBookingMode?: RECURRING_BOOKING_MODE;
  recurringBillingInterval?: RECURRENCE_INTERVAL | null;
  recurringHorizonDays?: number | null;
  providerId?: string;
  isSystem: boolean;
  createdAt: string;
};

export type ServiceTemplatesResponse = {
  system: ServiceTemplate[];
  provider: ServiceTemplate[];
};

export const serviceTemplateKeys = {
  all: ['service-templates'] as const,
};

export function useServiceTemplates() {
  const isHydrated = useAuthHydration();

  return useQuery({
    queryKey: serviceTemplateKeys.all,
    queryFn: async () => {
      const res = await api.get<ServiceTemplatesResponse>('/provider/service-templates');
      return res.data;
    },
    enabled: isHydrated,
  });
}

export type CreateServiceTemplateDto = {
  name: string;
  description?: string;
  defaultPrice?: number;
  priceDisplayMode?: PRICE_DISPLAY_MODE;
  address?: string;
  allowedPaymentMethods?: string[];
  durationMinutes?: number | null;
  slotCapacity?: number;
  isSubscription?: boolean;
  subscriptionQuota?: number | null;
  subscriptionQuotaPeriod?: QUOTA_PERIOD | null;
  recurringBookingMode?: RECURRING_BOOKING_MODE;
  recurringBillingInterval?: RECURRENCE_INTERVAL | null;
  recurringHorizonDays?: number | null;
};

export function useCreateServiceTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dto: CreateServiceTemplateDto) => {
      const res = await api.post<ServiceTemplate>('/provider/service-templates', dto);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serviceTemplateKeys.all });
    },
  });
}

export function useDeleteServiceTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/provider/service-templates/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serviceTemplateKeys.all });
    },
  });
}
